import React, { useEffect, useState } from 'react';
import {
  Save,
  FlaskConical,
  ArrowLeft,
  Plus,
  Trash2,
  Search,
  FileText,
  Layers,
  MessageSquare,
  Sliders,
  ShieldCheck,
  X,
  Check,
  BookOpen,
  Workflow,
  Database,
  BarChart3,
  Hash,
} from 'lucide-react';
import type { Theme, ThemeColors } from './ChatbotIntentsListing';
import IntentAnalysisSimulation, { type SimulationContext } from './IntentAnalysisSimulation';
import intentDefinitionService, {
  type DataModelItem,
  type KnowledgeBaseItem,
  type OrchestrationItem,
  type ReportCatalogItem,
  type WorkflowStartContract,
} from './intentDefinitionService';
import {
  mapStartContractFieldsToParameters,
  mergePendingGeneralSearchKeywordDraft,
  mergePendingLiveDataModelKeywordDraft,
  mergePendingLiveDataModelParameterDraft,
  mergePendingOrchestrationKeywordDrafts,
  mergePendingReportKeywordDrafts,
  mergePendingReportParameterDrafts,
  type IntentClassification,
  type IntentFormSnapshot,
  type ParameterSlot,
  type ReportDeliveryMode,
  type SelectedOrchestrationMapping,
  type SelectedReportMapping,
} from './intentDefinitionPayload';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800/80',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-900',
    border: 'border-neutral-800',
    text: { primary: 'text-neutral-100', secondary: 'text-neutral-300', muted: 'text-neutral-500' },
    accent: 'bg-slate-700 hover:bg-slate-600',
    hover: 'hover:bg-neutral-800/80',
    input: 'bg-neutral-900 border-neutral-700 text-neutral-100 placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-slate-50',
    background: 'bg-slate-100',
    surface: 'bg-white',
    border: 'border-slate-200',
    text: { primary: 'text-slate-900', secondary: 'text-slate-600', muted: 'text-slate-500' },
    accent: 'bg-slate-800 hover:bg-slate-900',
    hover: 'hover:bg-slate-50',
    input: 'bg-white border-slate-300 text-slate-900 placeholder-slate-400',
  },
};

/**
 * Unified UI tokens. Prefer slate/neutral tokens that read correctly for each theme.
 * Avoid `text-white` in portal-content — white portal CSS remaps it to near-black.
 */
const PRO_UI = {
  dark: {
    selectedRow: 'border-slate-600 bg-slate-800/50',
    selectedCheck: 'bg-slate-500 border-slate-500',
    checkIcon: 'text-slate-50',
    idBadge: 'bg-slate-800 text-slate-300 border border-slate-600',
    configHeader: 'bg-slate-800/60',
    keywordTag: 'bg-slate-800/80 text-slate-300 border border-slate-700',
    addBtn: 'border-slate-600 text-slate-300 hover:bg-slate-800',
    paramRequired: 'bg-rose-950/40 text-rose-300 border-rose-800/60',
    paramOptional: 'bg-slate-800/80 text-slate-300 border-slate-700',
    cardSelected: 'border-slate-500 bg-slate-800/50 ring-1 ring-slate-600/40',
    cardSelectedTitle: 'text-neutral-100',
    cardSelectedIcon: 'text-slate-300',
    badge: 'border-slate-600 text-slate-400 bg-slate-800/50',
    eyebrow: 'text-slate-400',
    btnGhost: 'border-slate-600 text-slate-300 hover:bg-slate-800',
    btnSolid: 'bg-slate-600 hover:bg-slate-500 text-slate-50',
    btnSave: 'border border-slate-600 bg-slate-100 text-slate-800 hover:bg-slate-200 font-medium',
    iconBox: 'bg-slate-800 text-slate-400',
    codeBlock: 'bg-slate-950 text-slate-300 border-slate-800',
    toggleOn: 'bg-slate-600',
    toggleOff: 'bg-neutral-600',
    statusActive: 'bg-slate-600 text-slate-50',
    dashedAdd: 'border-slate-600 text-slate-400 hover:bg-slate-800/50',
    focusRing: 'focus:ring-slate-500/40',
    deleteIcon: 'text-rose-400 hover:text-rose-300',
    checkbox: 'rounded w-3 h-3 shrink-0 accent-slate-400 border border-neutral-500 bg-neutral-800',
  },
  light: {
    selectedRow: 'border-slate-400 bg-slate-100',
    selectedCheck: 'bg-slate-700 border-slate-700',
    checkIcon: 'text-slate-50',
    idBadge: 'bg-slate-100 text-slate-700 border border-slate-200',
    configHeader: 'bg-slate-50',
    keywordTag: 'bg-slate-100 text-slate-800 border border-slate-200',
    addBtn: 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
    paramRequired: 'bg-rose-50 text-rose-800 border-rose-200',
    paramOptional: 'bg-slate-100 text-slate-700 border-slate-200',
    cardSelected: 'border-slate-500 bg-white ring-1 ring-slate-300 shadow-sm',
    cardSelectedTitle: 'text-slate-900',
    cardSelectedIcon: 'text-slate-700',
    badge: 'border-slate-200 text-slate-600 bg-slate-50',
    eyebrow: 'text-slate-500',
    btnGhost: 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
    btnSolid: 'bg-slate-800 hover:bg-slate-900 text-slate-50 shadow-sm',
    btnSave: 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 shadow-sm ring-1 ring-slate-200 font-medium',
    iconBox: 'bg-slate-100 text-slate-700',
    codeBlock: 'bg-slate-900 text-slate-100 border-slate-700',
    toggleOn: 'bg-slate-700',
    toggleOff: 'bg-slate-200',
    statusActive: 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-300',
    dashedAdd: 'border-slate-300 text-slate-700 hover:bg-slate-50',
    focusRing: 'focus:ring-slate-400/50',
    deleteIcon: 'text-rose-600 hover:text-rose-700',
    checkbox: 'rounded w-3 h-3 shrink-0 accent-slate-600 bg-white border border-slate-300',
  },
} as const;

const CLASSIFICATION_OPTIONS: {
  value: IntentClassification;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'requesting_action',
    label: 'Requesting Action',
    description: 'User wants to execute a workflow, trigger automation, or perform an operation.',
    icon: <Workflow className="w-3.5 h-3.5" />,
  },
  {
    value: 'requesting_report',
    label: 'Report (Analytics Hub)',
    description: 'User asks for a saved report or dashboard from Analytics Hub.',
    icon: <BarChart3 className="w-3.5 h-3.5" />,
  },
  {
    value: 'live_report',
    label: 'Live Report',
    description: 'User asks for real-time data queried directly from a data model.',
    icon: <Database className="w-3.5 h-3.5" />,
  },
  {
    value: 'general_search',
    label: 'General Search',
    description: 'User seeks explanations, policies, definitions, or knowledge base answers.',
    icon: <BookOpen className="w-3.5 h-3.5" />,
  },
];

const LIVE_REPORT_CLAIM_OPTIONS = [
  '$user_id',
  '$department',
  '$department_id',
  '$role',
  '$role_id',
  '$tenant_id',
  '$email',
  '$sub',
] as const;
const CUSTOM_CLAIM_OPTION = '__custom_claim__';
type LiveParameterType = 'claim' | 'parameter';

const LIVE_REPORT_DATA_TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'uuid', label: 'UUID' },
  { value: 'array', label: 'Array' },
  { value: 'json', label: 'JSON' },
] as const;

export interface IntentDefinitionProps {
  theme?: Theme;
  chatbotId?: string;
  onBack: () => void;
  onSave?: () => void;
  mode?: 'create' | 'edit';
  intentId?: string;
}

const Toggle: React.FC<{
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  colors: ThemeColors;
  toggleOnClass: string;
  toggleOffClass: string;
}> = ({ checked, onChange, label, colors, toggleOnClass, toggleOffClass }) => (
  <label className="flex items-center justify-between gap-3 cursor-pointer">
    <span className={`text-[11px] ${colors.text.secondary}`}>{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
        checked ? toggleOnClass : toggleOffClass
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  </label>
);

const Card: React.FC<{
  title: string;
  icon: React.ReactNode;
  colors: ThemeColors;
  children: React.ReactNode;
  badge?: React.ReactNode;
  subtitle?: string;
  iconBoxClass?: string;
}> = ({ title, icon, colors, children, badge, subtitle, iconBoxClass }) => (
  <section className={`${colors.surface} border ${colors.border} rounded-lg overflow-hidden shadow-sm`}>
    <div className={`px-3.5 py-2.5 border-b ${colors.border} flex items-center justify-between gap-2`}>
      <div className="flex items-center gap-2 min-w-0">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${iconBoxClass ?? 'bg-slate-100 text-slate-600'}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <h2 className={`text-xs font-semibold ${colors.text.primary} leading-tight`}>{title}</h2>
          {subtitle && <p className={`text-[10px] ${colors.text.muted} truncate`}>{subtitle}</p>}
        </div>
      </div>
      {badge}
    </div>
    <div className="p-3.5">{children}</div>
  </section>
);

function readPortalTheme(fallback: Theme = 'dark'): Theme {
  if (typeof document === 'undefined') return fallback;
  const activeTheme = document.documentElement.getAttribute('data-theme');
  if (activeTheme === 'white') return 'light';
  if (activeTheme === 'black') return 'dark';
  return fallback;
}

const IntentDefinition: React.FC<IntentDefinitionProps> = ({
  theme: themeProp = 'dark',
  chatbotId,
  onBack,
  onSave,
  mode = 'create',
  intentId,
}) => {
  const [theme, setTheme] = useState<Theme>(() => readPortalTheme(themeProp));
  const colors = THEME_CONFIG[theme];
  const pro = PRO_UI[theme];

  useEffect(() => {
    setTheme(readPortalTheme(themeProp));
  }, [themeProp]);

  useEffect(() => {
    const syncTheme = () => setTheme(readPortalTheme(themeProp));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, [themeProp]);

  const [intentName, setIntentName] = useState('');
  const [intentCode, setIntentCode] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.75);
  const [classification, setClassification] = useState<IntentClassification>('requesting_report');
  const [loadingIntent, setLoadingIntent] = useState(mode === 'edit');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [orchestrations, setOrchestrations] = useState<OrchestrationItem[]>([]);
  const [orchestrationsLoading, setOrchestrationsLoading] = useState(false);
  const [orchestrationsError, setOrchestrationsError] = useState<string | null>(null);
  const [selectedOrchestrationMappings, setSelectedOrchestrationMappings] = useState<SelectedOrchestrationMapping[]>([]);
  const [orchSearch, setOrchSearch] = useState('');
  const [orchKeywordDraft, setOrchKeywordDraft] = useState<Record<string, string>>({});
  const [workflowStartContract, setWorkflowStartContract] = useState<WorkflowStartContract | null>(null);
  const [startContractLoading, setStartContractLoading] = useState(false);
  const [startContractError, setStartContractError] = useState<string | null>(null);

  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseItem[]>([]);
  const [knowledgeBasesLoading, setKnowledgeBasesLoading] = useState(false);
  const [knowledgeBasesError, setKnowledgeBasesError] = useState<string | null>(null);
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState<string[]>([]);
  const [generalSearchKeywords, setGeneralSearchKeywords] = useState<string[]>([]);
  const [generalSearchKeywordDraft, setGeneralSearchKeywordDraft] = useState('');
  const [kbSearch, setKbSearch] = useState('');

  const [reportDeliveryMode, setReportDeliveryMode] = useState<ReportDeliveryMode>('analytics_hub');
  const [dataModels, setDataModels] = useState<DataModelItem[]>([]);
  const [dataModelsLoading, setDataModelsLoading] = useState(false);
  const [dataModelsError, setDataModelsError] = useState<string | null>(null);
  const [selectedDataModel, setSelectedDataModel] = useState('');
  const [liveReportInstructions, setLiveReportInstructions] = useState('');
  const [liveDataModelMapping, setLiveDataModelMapping] = useState<{ keywords: string[]; parameters: ParameterSlot[] }>({
    keywords: [],
    parameters: [],
  });
  const [liveParamDraft, setLiveParamDraft] = useState({
    name: '',
    parameterType: 'claim' as LiveParameterType,
    claimOption: LIVE_REPORT_CLAIM_OPTIONS[0] as string,
    customClaim: '',
    dataType: LIVE_REPORT_DATA_TYPE_OPTIONS[0].value as string,
    arrayValues: [] as string[],
    arrayValueDraft: '',
    required: false,
  });
  const [liveKeywordDraft, setLiveKeywordDraft] = useState('');
  const [reportCatalog, setReportCatalog] = useState<ReportCatalogItem[]>([]);
  const [reportCatalogLoading, setReportCatalogLoading] = useState(false);
  const [reportCatalogError, setReportCatalogError] = useState<string | null>(null);
  const [selectedReportMappings, setSelectedReportMappings] = useState<SelectedReportMapping[]>([]);
  const [reportSearch, setReportSearch] = useState('');
  const [paramDraft, setParamDraft] = useState<Record<string, { name: string; required: boolean }>>({});
  const [keywordDraft, setKeywordDraft] = useState<Record<string, string>>({});

  const [trainingPhrases, setTrainingPhrases] = useState<string[]>(['']);

  const [confirmBelowThreshold, setConfirmBelowThreshold] = useState(true);
  const [confirmMultipleRoutes, setConfirmMultipleRoutes] = useState(true);
  const [confirmMissingParams, setConfirmMissingParams] = useState(true);
  const [confirmationThreshold, setConfirmationThreshold] = useState(80);
  const [confirmationMessage, setConfirmationMessage] = useState(
    'I found a possible match. Would you like to proceed or refine your request?'
  );
  const [testMessage, setTestMessage] = useState('');
  const [simulationRunToken, setSimulationRunToken] = useState(0);
  const [simulationContext, setSimulationContext] = useState<SimulationContext | null>(null);
  const [simulationOpen, setSimulationOpen] = useState(false);
  const [lastSimulationSummary, setLastSimulationSummary] = useState<string | null>(null);

  const inputClass = `w-full ${colors.input} border rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 ${pro.focusRing}`;
  const selectClass = inputClass;
  const labelClass = `block text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-1`;
  const isLiveParameterClaim = liveParamDraft.parameterType === 'claim';

  const filteredOrchestrations = orchestrations.filter((o) => {
    const q = orchSearch.toLowerCase();
    return (
      o.name.toLowerCase().includes(q) ||
      o.description.toLowerCase().includes(q) ||
      o.id.toLowerCase().includes(q) ||
      (o.code ?? '').toLowerCase().includes(q)
    );
  });
  const filteredKnowledgeBases = knowledgeBases.filter(
    (k) => k.name.toLowerCase().includes(kbSearch.toLowerCase()) || k.scope.toLowerCase().includes(kbSearch.toLowerCase())
  );

  useEffect(() => {
    if (classification !== 'general_search') return;

    let cancelled = false;
    (async () => {
      setKnowledgeBasesLoading(true);
      setKnowledgeBasesError(null);
      try {
        const list = await intentDefinitionService.getKnowledgeBases();
        if (!cancelled) setKnowledgeBases(list);
      } catch (e) {
        if (!cancelled) {
          setKnowledgeBases([]);
          setKnowledgeBasesError(e instanceof Error ? e.message : 'Failed to load knowledge bases');
        }
      } finally {
        if (!cancelled) setKnowledgeBasesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [classification]);

  useEffect(() => {
    if (classification !== 'requesting_action') return;

    let cancelled = false;
    (async () => {
      setOrchestrationsLoading(true);
      setOrchestrationsError(null);
      try {
        const list = await intentDefinitionService.getOrchestrations();
        if (!cancelled) setOrchestrations(list);
      } catch (e) {
        if (!cancelled) {
          setOrchestrations([]);
          setOrchestrationsError(e instanceof Error ? e.message : 'Failed to load orchestrations');
        }
      } finally {
        if (!cancelled) setOrchestrationsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [classification]);

  const selectedOrchestrationId = selectedOrchestrationMappings[0]?.orchestrationId ?? '';
  const selectedOrchestrationChannel =
    orchestrations.find((item) => item.id === selectedOrchestrationId)?.defaultChannel || 'sandbox';

  useEffect(() => {
    if (classification !== 'requesting_action') {
      setWorkflowStartContract(null);
      setStartContractError(null);
      setStartContractLoading(false);
      return;
    }

    if (!selectedOrchestrationId) {
      setWorkflowStartContract(null);
      setStartContractError(null);
      setStartContractLoading(false);
      return;
    }

    if (workflowStartContract?.workflowId === selectedOrchestrationId) {
      setStartContractLoading(false);
      return;
    }

    let cancelled = false;
    setStartContractLoading(true);
    setStartContractError(null);

    (async () => {
      try {
        const contract = await intentDefinitionService.getWorkflowStartContract(
          selectedOrchestrationId,
          selectedOrchestrationChannel
        );
        if (cancelled) return;
        setWorkflowStartContract(contract);
        const parameters = mapStartContractFieldsToParameters(contract.start.fields);
        setSelectedOrchestrationMappings((prev) => {
          const current = prev[0];
          if (!current || current.orchestrationId !== selectedOrchestrationId) return prev;
          return [{ ...current, parameters }];
        });
      } catch (e) {
        if (cancelled) return;
        setWorkflowStartContract(null);
        setStartContractError(e instanceof Error ? e.message : 'Failed to load workflow start contract');
      } finally {
        if (!cancelled) setStartContractLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    classification,
    selectedOrchestrationId,
    selectedOrchestrationChannel,
    workflowStartContract?.workflowId,
  ]);

  useEffect(() => {
    if (classification !== 'live_report') return;

    let cancelled = false;
    (async () => {
      setDataModelsLoading(true);
      setDataModelsError(null);
      try {
        const list = await intentDefinitionService.getDataModels();
        if (!cancelled) {
          setDataModels(list);
          setSelectedDataModel((prev) => prev || list[0]?.id || '');
        }
      } catch (e) {
        if (!cancelled) {
          setDataModels([]);
          setDataModelsError(e instanceof Error ? e.message : 'Failed to load data models');
        }
      } finally {
        if (!cancelled) setDataModelsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [classification]);

  useEffect(() => {
    if (classification !== 'requesting_report') return;

    let cancelled = false;
    (async () => {
      setReportCatalogLoading(true);
      setReportCatalogError(null);
      try {
        const list = await intentDefinitionService.getReportDefinitions();
        if (!cancelled) setReportCatalog(list);
      } catch (e) {
        if (!cancelled) {
          setReportCatalog([]);
          setReportCatalogError(e instanceof Error ? e.message : 'Failed to load reports');
        }
      } finally {
        if (!cancelled) setReportCatalogLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [classification]);

  const filteredCatalogReports = reportCatalog.filter(
    (r) =>
      r.name.toLowerCase().includes(reportSearch.toLowerCase()) ||
      r.description.toLowerCase().includes(reportSearch.toLowerCase())
  );

  const applyParsedIntent = (parsed: Partial<IntentFormSnapshot>) => {
    if (parsed.intentName != null) setIntentName(parsed.intentName);
    if (parsed.intentCode != null) setIntentCode(parsed.intentCode);
    if (parsed.description != null) setDescription(parsed.description);
    if (parsed.isActive != null) setIsActive(parsed.isActive);
    if (parsed.confidenceThreshold != null) setConfidenceThreshold(parsed.confidenceThreshold);
    if (parsed.classification != null) setClassification(parsed.classification);
    if (parsed.selectedOrchestrationMappings != null) {
      setSelectedOrchestrationMappings(parsed.selectedOrchestrationMappings.slice(0, 1));
      setWorkflowStartContract(null);
      setStartContractError(null);
      setStartContractLoading(false);
    }
    if (parsed.reportDeliveryMode != null) setReportDeliveryMode(parsed.reportDeliveryMode);
    if (parsed.selectedDataModel != null) setSelectedDataModel(parsed.selectedDataModel);
    if (parsed.liveReportInstructions != null) {
      setLiveReportInstructions(parsed.liveReportInstructions);
    } else if (parsed.classification === 'live_report') {
      setLiveReportInstructions('');
    }
    if (parsed.liveDataModelMapping != null) {
      setLiveDataModelMapping({
        keywords: Array.isArray(parsed.liveDataModelMapping.keywords)
          ? parsed.liveDataModelMapping.keywords
          : [],
        parameters: Array.isArray(parsed.liveDataModelMapping.parameters)
          ? parsed.liveDataModelMapping.parameters
          : [],
      });
    } else if (parsed.classification === 'live_report') {
      setLiveDataModelMapping({ keywords: [], parameters: [] });
    }
    if (parsed.selectedReportMappings != null) setSelectedReportMappings(parsed.selectedReportMappings);
    if (parsed.selectedKnowledgeBases != null) setSelectedKnowledgeBases(parsed.selectedKnowledgeBases);
    if (parsed.generalSearchKeywords != null) setGeneralSearchKeywords(parsed.generalSearchKeywords);
    if (parsed.trainingPhrases != null) {
      setTrainingPhrases(parsed.trainingPhrases.length > 0 ? parsed.trainingPhrases : ['']);
    }
    if (parsed.confirmBelowThreshold != null) setConfirmBelowThreshold(parsed.confirmBelowThreshold);
    if (parsed.confirmationThreshold != null) setConfirmationThreshold(parsed.confirmationThreshold);
    if (parsed.confirmMultipleRoutes != null) setConfirmMultipleRoutes(parsed.confirmMultipleRoutes);
    if (parsed.confirmMissingParams != null) setConfirmMissingParams(parsed.confirmMissingParams);
    if (parsed.confirmationMessage != null) setConfirmationMessage(parsed.confirmationMessage);
  };

  useEffect(() => {
    if (mode !== 'edit' || !intentId) return;

    let cancelled = false;
    (async () => {
      setLoadingIntent(true);
      setLoadError(null);
      try {
        const loaded = await intentDefinitionService.loadIntentForEdit(intentId);
        if (!cancelled) {
          applyParsedIntent(loaded.snapshot);
          if (loaded.orchestrationItems.length > 0) {
            setOrchestrations((prev) => intentDefinitionService.mergeCatalogById(prev, loaded.orchestrationItems));
          }
          if (loaded.reportItems.length > 0) {
            setReportCatalog((prev) => intentDefinitionService.mergeCatalogById(prev, loaded.reportItems));
          }
          if (loaded.knowledgeBaseItems.length > 0) {
            setKnowledgeBases((prev) => intentDefinitionService.mergeCatalogById(prev, loaded.knowledgeBaseItems));
          }
          if (loaded.dataModelItems.length > 0) {
            setDataModels((prev) => intentDefinitionService.mergeCatalogById(prev, loaded.dataModelItems));
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Failed to load intent');
        }
      } finally {
        if (!cancelled) setLoadingIntent(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, intentId]);

  const getPendingLiveArrayValues = () => {
    const pending = liveParamDraft.arrayValueDraft.trim();
    if (
      !pending ||
      liveParamDraft.arrayValues.some((value) => value.toLowerCase() === pending.toLowerCase())
    ) {
      return liveParamDraft.arrayValues;
    }
    return [...liveParamDraft.arrayValues, pending];
  };

  const getPendingLiveParameter = () => {
    const isClaim = liveParamDraft.parameterType === 'claim';
    const claim = isClaim
      ? liveParamDraft.claimOption === CUSTOM_CLAIM_OPTION
        ? liveParamDraft.customClaim.trim()
        : liveParamDraft.claimOption
      : '';
    const validClaim = !isClaim || (claim.startsWith('$') && claim.length > 1);
    return {
      name: validClaim ? liveParamDraft.name : '',
      parameterType: liveParamDraft.parameterType,
      ...(isClaim
        ? { claim }
        : {
            dataType: liveParamDraft.dataType,
            ...(liveParamDraft.dataType === 'array'
              ? { values: getPendingLiveArrayValues() }
              : {}),
          }),
      required: isClaim ? false : liveParamDraft.required,
    };
  };

  const buildFormSnapshot = (): IntentFormSnapshot => {
    const mergedOrchestrationMappings = mergePendingOrchestrationKeywordDrafts(
      selectedOrchestrationMappings,
      orchKeywordDraft
    ).slice(0, 1);
    const mergedReportMappings = mergePendingReportParameterDrafts(
      mergePendingReportKeywordDrafts(selectedReportMappings, keywordDraft),
      paramDraft
    );
    const mergedGeneralSearchKeywords = mergePendingGeneralSearchKeywordDraft(
      generalSearchKeywords,
      generalSearchKeywordDraft
    );
    const mergedLiveDataModelMapping = mergePendingLiveDataModelParameterDraft(
      mergePendingLiveDataModelKeywordDraft(liveDataModelMapping, liveKeywordDraft),
      getPendingLiveParameter()
    );

    return {
      intentId: mode === 'edit' ? intentId : undefined,
      chatbotId: chatbotId ?? '',
      intentName,
      intentCode,
      description,
      isActive,
      confidenceThreshold,
      classification,
      selectedOrchestrationMappings: mergedOrchestrationMappings,
      orchestrations,
      reportDeliveryMode,
      askClarifyingQuestion: false,
      clarifyingQuestion: '',
      parameterPrompt: '',
      selectedDataModel,
      liveReportInstructions,
      liveDataModelMapping: mergedLiveDataModelMapping,
      dataModels,
      selectedReportMappings: mergedReportMappings,
      reportCatalog,
      selectedKnowledgeBases,
      knowledgeBases,
      generalSearchKeywords: mergedGeneralSearchKeywords,
      trainingPhrases,
      confirmBelowThreshold,
      confirmationThreshold,
      confirmMultipleRoutes,
      confirmMissingParams,
      confirmationMessage,
    };
  };

  const commitPendingDraftsToState = () => {
    setSelectedOrchestrationMappings((prev) =>
      mergePendingOrchestrationKeywordDrafts(prev, orchKeywordDraft).slice(0, 1)
    );
    setOrchKeywordDraft({});

    setSelectedReportMappings((prev) =>
      mergePendingReportParameterDrafts(
        mergePendingReportKeywordDrafts(prev, keywordDraft),
        paramDraft
      )
    );
    setKeywordDraft({});
    setParamDraft({});

    setGeneralSearchKeywords((prev) => mergePendingGeneralSearchKeywordDraft(prev, generalSearchKeywordDraft));
    setGeneralSearchKeywordDraft('');

    setLiveDataModelMapping((prev) =>
      mergePendingLiveDataModelParameterDraft(
        mergePendingLiveDataModelKeywordDraft(prev, liveKeywordDraft),
        getPendingLiveParameter()
      )
    );
    setLiveKeywordDraft('');
    setLiveParamDraft({
      name: '',
      parameterType: 'claim',
      claimOption: LIVE_REPORT_CLAIM_OPTIONS[0],
      customClaim: '',
      dataType: LIVE_REPORT_DATA_TYPE_OPTIONS[0].value,
      arrayValues: [],
      arrayValueDraft: '',
      required: false,
    });
  };

  const handleSave = async () => {
    setSaveError(null);
    setDeleteError(null);
    const snapshot = buildFormSnapshot();
    const validationError = intentDefinitionService.validateIntentForm(snapshot);
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    setSaving(true);
    try {
      await intentDefinitionService.upsertIntentDefinition(snapshot);
      commitPendingDraftsToState();
      onSave?.();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save intent');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (mode !== 'edit' || !intentId?.trim()) return;

    const label = intentName.trim() || intentId;
    if (
      !window.confirm(
        `Are you sure you want to delete "${label}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeleteError(null);
    setSaveError(null);
    setDeleting(true);
    try {
      await intentDefinitionService.deleteIntentDefinition(intentId);
      onBack?.();
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete intent');
    } finally {
      setDeleting(false);
    }
  };

  const isReportSelected = (reportId: string) => selectedReportMappings.some((m) => m.reportId === reportId);
  const getCatalogReport = (reportId: string) => reportCatalog.find((r) => r.id === reportId);

  const isOrchestrationSelected = (id: string) =>
    selectedOrchestrationMappings.some((m) => m.orchestrationId === id);
  const getOrchestration = (id: string) => orchestrations.find((o) => o.id === id);
  const selectedOrchestrationMapping = selectedOrchestrationMappings[0] ?? null;

  const clearWorkflowSelection = () => {
    setSelectedOrchestrationMappings([]);
    setWorkflowStartContract(null);
    setStartContractError(null);
    setStartContractLoading(false);
  };

  const selectOrchestration = (id: string) => {
    if (selectedOrchestrationMappings.some((m) => m.orchestrationId === id)) {
      clearWorkflowSelection();
      return;
    }
    setWorkflowStartContract(null);
    setStartContractError(null);
    setSelectedOrchestrationMappings([{ orchestrationId: id, keywords: [], parameters: [] }]);
  };

  const getOrchKeywordDraft = (orchId: string) => orchKeywordDraft[orchId] ?? '';

  const addOrchKeyword = (orchId: string, keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setSelectedOrchestrationMappings((prev) =>
      prev.map((m) => {
        if (m.orchestrationId !== orchId || m.keywords.some((k) => k.toLowerCase() === trimmed.toLowerCase())) return m;
        return { ...m, keywords: [...m.keywords, trimmed] };
      })
    );
    setOrchKeywordDraft((prev) => ({ ...prev, [orchId]: '' }));
  };

  const removeOrchKeyword = (orchId: string, keyword: string) => {
    setSelectedOrchestrationMappings((prev) =>
      prev.map((m) =>
        m.orchestrationId === orchId ? { ...m, keywords: m.keywords.filter((k) => k !== keyword) } : m
      )
    );
  };

  const removeSelectedOrchestration = () => {
    clearWorkflowSelection();
  };

  const toggleKnowledgeBase = (id: string) => {
    setSelectedKnowledgeBases((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addGeneralSearchKeyword = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setGeneralSearchKeywords((prev) =>
      prev.some((item) => item.toLowerCase() === trimmed.toLowerCase()) ? prev : [...prev, trimmed]
    );
    setGeneralSearchKeywordDraft('');
  };

  const removeGeneralSearchKeyword = (keyword: string) => {
    setGeneralSearchKeywords((prev) => prev.filter((item) => item !== keyword));
  };

  const addLiveKeyword = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setLiveDataModelMapping((prev) => ({
      ...prev,
      keywords: prev.keywords.some((item) => item.toLowerCase() === trimmed.toLowerCase())
        ? prev.keywords
        : [...prev.keywords, trimmed],
    }));
    setLiveKeywordDraft('');
  };

  const removeLiveKeyword = (keyword: string) => {
    setLiveDataModelMapping((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((item) => item !== keyword),
    }));
  };

  const addLiveArrayValue = () => {
    const value = liveParamDraft.arrayValueDraft.trim();
    if (!value) return;
    setLiveParamDraft((prev) => ({
      ...prev,
      arrayValues: prev.arrayValues.some(
        (existing) => existing.toLowerCase() === value.toLowerCase()
      )
        ? prev.arrayValues
        : [...prev.arrayValues, value],
      arrayValueDraft: '',
    }));
  };

  const removeLiveArrayValue = (value: string) => {
    setLiveParamDraft((prev) => ({
      ...prev,
      arrayValues: prev.arrayValues.filter((existing) => existing !== value),
    }));
  };

  const addLiveParameter = () => {
    const name = liveParamDraft.name.trim();
    const isClaim = liveParamDraft.parameterType === 'claim';
    const claim = isClaim
      ? liveParamDraft.claimOption === CUSTOM_CLAIM_OPTION
        ? liveParamDraft.customClaim.trim()
        : liveParamDraft.claimOption
      : '';
    const arrayValues = getPendingLiveArrayValues();
    if (
      !name ||
      (isClaim && (!claim.startsWith('$') || claim.length <= 1)) ||
      (!isClaim && liveParamDraft.dataType === 'array' && arrayValues.length === 0)
    ) return;
    setLiveDataModelMapping((prev) => ({
      ...prev,
      parameters: prev.parameters.some((param) => param.name.toLowerCase() === name.toLowerCase())
        ? prev.parameters
        : [
            ...prev.parameters,
            {
              id: `lp-${Date.now()}`,
              name,
              parameterType: liveParamDraft.parameterType,
              ...(isClaim
                ? { claim }
                : {
                    dataType: liveParamDraft.dataType,
                    ...(liveParamDraft.dataType === 'array' ? { values: arrayValues } : {}),
                  }),
              required: isClaim ? false : liveParamDraft.required,
            },
          ],
    }));
    setLiveParamDraft({
      name: '',
      parameterType: 'claim',
      claimOption: LIVE_REPORT_CLAIM_OPTIONS[0],
      customClaim: '',
      dataType: LIVE_REPORT_DATA_TYPE_OPTIONS[0].value,
      arrayValues: [],
      arrayValueDraft: '',
      required: false,
    });
  };

  const toggleLiveParameterRequired = (paramId: string) => {
    setLiveDataModelMapping((prev) => ({
      ...prev,
      parameters: prev.parameters.map((param) =>
        param.id === paramId && (param.parameterType ?? (param.claim ? 'claim' : 'parameter')) === 'parameter'
          ? { ...param, required: !param.required }
          : param
      ),
    }));
  };

  const removeLiveParameter = (paramId: string) => {
    setLiveDataModelMapping((prev) => ({
      ...prev,
      parameters: prev.parameters.filter((param) => param.id !== paramId),
    }));
  };

  const toggleReportSelection = (reportId: string) => {
    setSelectedReportMappings((prev) => {
      if (prev.some((m) => m.reportId === reportId)) return prev.filter((m) => m.reportId !== reportId);
      return [...prev, { reportId, keywords: [], parameters: [] }];
    });
  };

  const getParamDraft = (reportId: string) => paramDraft[reportId] ?? { name: '', required: false };
  const setParamDraftForReport = (reportId: string, patch: Partial<{ name: string; required: boolean }>) => {
    setParamDraft((prev) => ({ ...prev, [reportId]: { ...getParamDraft(reportId), ...patch } }));
  };

  const addReportParameter = (reportId: string, name: string, required: boolean) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSelectedReportMappings((prev) =>
      prev.map((m) => {
        if (m.reportId !== reportId || m.parameters.some((p) => p.name === trimmed)) return m;
        return {
          ...m,
          parameters: [...m.parameters, { id: `param-${Date.now()}`, name: trimmed, required }],
        };
      })
    );
    setParamDraftForReport(reportId, { name: '', required: false });
  };

  const addReportKeyword = (reportId: string, keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setSelectedReportMappings((prev) =>
      prev.map((m) => {
        if (m.reportId !== reportId || m.keywords.some((k) => k.toLowerCase() === trimmed.toLowerCase())) return m;
        return { ...m, keywords: [...m.keywords, trimmed] };
      })
    );
    setKeywordDraft((prev) => ({ ...prev, [reportId]: '' }));
  };

  const toggleParameterRequired = (reportId: string, paramId: string) => {
    setSelectedReportMappings((prev) =>
      prev.map((m) =>
        m.reportId === reportId
          ? { ...m, parameters: m.parameters.map((p) => (p.id === paramId ? { ...p, required: !p.required } : p)) }
          : m
      )
    );
  };

  const removeReportParameter = (reportId: string, paramId: string) => {
    setSelectedReportMappings((prev) =>
      prev.map((m) =>
        m.reportId === reportId ? { ...m, parameters: m.parameters.filter((p) => p.id !== paramId) } : m
      )
    );
  };

  const removeReportKeyword = (reportId: string, keyword: string) => {
    setSelectedReportMappings((prev) =>
      prev.map((m) =>
        m.reportId === reportId ? { ...m, keywords: m.keywords.filter((k) => k !== keyword) } : m
      )
    );
  };

  const removeSelectedReport = (reportId: string) => {
    setSelectedReportMappings((prev) => prev.filter((m) => m.reportId !== reportId));
  };

  const selectedClassification = CLASSIFICATION_OPTIONS.find((c) => c.value === classification)!;

  const handleAnalyze = () => {
    const orchMapping = selectedOrchestrationMappings[0];
    const orchItem = orchMapping
      ? orchestrations.find((o) => o.id === orchMapping.orchestrationId)
      : undefined;
    const reportMapping = selectedReportMappings[0];
    const reportItem = reportMapping
      ? reportCatalog.find((r) => r.id === reportMapping.reportId)
      : undefined;
    const dataModel = dataModels.find((d) => d.id === selectedDataModel);

    const context: SimulationContext = {
      query: testMessage,
      intentCode,
      intentName,
      classification,
      classificationLabel: selectedClassification.label,
      confidence: confidenceThreshold,
    };

    if (classification === 'requesting_action' && orchItem && orchMapping) {
      context.orchestration = {
        id: orchItem.id,
        name: orchItem.name,
        parameters: orchMapping.parameters.map((p) => p.name),
      };
    } else if (classification === 'requesting_report' || classification === 'live_report') {
      if (classification === 'requesting_report' && reportItem && reportMapping) {
        context.report = {
          id: reportItem.id,
          name: reportItem.name,
          parameters: reportMapping.parameters.map((p) => p.name),
        };
      } else if (classification === 'live_report' && dataModel) {
        context.dataModel = {
          id: dataModel.id,
          name: dataModel.name,
          entities: dataModel.entities,
        };
      }
    } else if (classification === 'general_search') {
      context.knowledgeBases = selectedKnowledgeBases
        .map((id) => knowledgeBases.find((k) => k.id === id))
        .filter((k): k is KnowledgeBaseItem => Boolean(k))
        .map((k) => ({ id: k.id, name: k.name }));
    }

    setSimulationContext(context);
    setSimulationRunToken((t) => t + 1);
    setSimulationOpen(true);
  };

  return (
    <div
      className={`h-full ${colors.background} overflow-auto text-xs`}
      style={{ colorScheme: theme === 'dark' ? 'dark' : 'light' }}
    >
      {/* Header */}
      <div className={`px-4 py-3 border-b ${colors.border} ${colors.primary} sticky top-0 z-10`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 max-w-[1400px] mx-auto">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${pro.eyebrow}`}>
                {mode === 'create' ? 'New Intent' : 'Edit Intent'}
              </span>
              {intentId && (
                <span className={`text-[10px] font-mono px-1 py-0.5 rounded ${pro.idBadge}`}>
                  {intentId}
                </span>
              )}
            </div>
            <h1 className={`text-sm font-semibold ${colors.text.primary}`}>Intent Definition</h1>
            <p className={`text-[11px] ${colors.text.muted} mt-0.5`}>
              Classify user intent and configure routing to orchestrations, reports, or knowledge sources.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={onBack}
              className={`px-3 py-1.5 border ${colors.border} ${colors.text.secondary} text-[11px] rounded-md flex items-center gap-1.5 ${colors.hover}`}
            >
              <ArrowLeft className="w-3 h-3" /> Back to Intents
            </button>
            <button
              type="button"
              onClick={handleAnalyze}
              className={`px-3 py-1.5 border text-[11px] rounded-md flex items-center gap-1.5 ${pro.btnGhost}`}
            >
              <FlaskConical className="w-3 h-3" /> Test
            </button>
            {mode === 'edit' && intentId && (
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={deleting || saving || loadingIntent}
                className={`px-3 py-1.5 border text-[11px] rounded-md flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed ${
                  theme === 'dark'
                    ? 'border-rose-800/60 text-rose-400 hover:bg-rose-950/40'
                    : 'border-rose-200 text-rose-600 hover:bg-rose-50'
                }`}
              >
                <Trash2 className="w-3 h-3" /> {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSave}
              disabled={saving || deleting || loadingIntent}
              className={`px-3 py-1.5 text-[11px] rounded-md flex items-center gap-1.5 ${pro.btnSave} disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save Intent'}
            </button>
          </div>
        </div>
        {(loadError || saveError || deleteError) && (
          <div className="max-w-[1400px] mx-auto mt-2 px-4">
            {loadError && <p className="text-[11px] text-rose-500">{loadError}</p>}
            {saveError && <p className="text-[11px] text-rose-500">{saveError}</p>}
            {deleteError && <p className="text-[11px] text-rose-500">{deleteError}</p>}
          </div>
        )}
      </div>

      <div className="p-4 max-w-[1400px] mx-auto">
        {loadingIntent ? (
          <p className={`text-xs ${colors.text.muted} italic py-6`}>Loading intent definition...</p>
        ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4">
          <div className="space-y-4">
            {/* Basic Info */}
            <Card title="Intent Profile" icon={<Sliders className="w-3 h-3" />} colors={colors} iconBoxClass={pro.iconBox}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Name</label>
                  <input type="text" value={intentName} onChange={(e) => setIntentName(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Code</label>
                  <input type="text" value={intentCode} onChange={(e) => setIntentCode(e.target.value)} className={`${inputClass} font-mono`} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Description</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <div className={`flex gap-1.5 p-1 rounded-md border ${colors.border} ${colors.secondary}`}>
                    {(['Active', 'Inactive'] as const).map((s) => {
                      const active = s === 'Active' ? isActive : !isActive;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setIsActive(s === 'Active')}
                          className={`flex-1 py-1 rounded text-[11px] font-medium transition-colors ${
                            active ? pro.statusActive : `${colors.text.muted} ${colors.hover}`
                          }`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Confidence Threshold</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={confidenceThreshold * 100}
                      onChange={(e) => setConfidenceThreshold(Number(e.target.value) / 100)}
                      className="flex-1 accent-slate-600 h-1"
                    />
                    <span className={`text-[11px] font-mono w-9 text-right ${colors.text.primary}`}>
                      {(confidenceThreshold * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Intent Classification */}
            <Card
              title="Intent Classification"
              icon={<Layers className="w-3 h-3" />}
              colors={colors}
              iconBoxClass={pro.iconBox}
              badge={
                <span className={`text-[10px] px-2 py-0.5 rounded border ${pro.badge}`}>
                  {selectedClassification.label}
                </span>
              }
              subtitle="Determines which routing configuration is shown below"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                {CLASSIFICATION_OPTIONS.map((opt) => {
                  const selected = classification === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setClassification(opt.value);
                        if (opt.value === 'requesting_report') setReportDeliveryMode('analytics_hub');
                        if (opt.value === 'live_report') setReportDeliveryMode('live');
                      }}
                      className={`text-left p-2.5 rounded-md border transition-all ${
                        selected
                          ? pro.cardSelected
                          : `${colors.border} ${colors.secondary} ${colors.hover}`
                      }`}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={selected ? pro.cardSelectedIcon : colors.text.muted}>{opt.icon}</span>
                        <span className={`text-[11px] font-semibold ${selected ? pro.cardSelectedTitle : colors.text.primary}`}>
                          {opt.label}
                        </span>
                      </div>
                      <p className={`text-[10px] leading-snug ${selected ? colors.text.secondary : colors.text.muted}`}>
                        {opt.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* ── Requesting Action → Orchestrations ── */}
            {classification === 'requesting_action' && (
              <Card
                title="Orchestration Routing"
                icon={<Workflow className="w-3 h-3" />}
                colors={colors}
                iconBoxClass={pro.iconBox}
                badge={
                  <span className={`text-[10px] ${colors.text.muted}`}>
                    {selectedOrchestrationMapping ? '1 selected' : 'none selected'}
                  </span>
                }
                subtitle="Pick one workflow to run when this intent is detected"
              >
                <div className="relative mb-2.5">
                  <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${colors.text.muted}`} />
                  <input
                    type="text"
                    value={orchSearch}
                    onChange={(e) => setOrchSearch(e.target.value)}
                    placeholder="Search workflows..."
                    className={`${inputClass} pl-7`}
                  />
                </div>

                <p className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-1.5`}>Catalog</p>
                {orchestrationsError && (
                  <p className="text-[10px] text-rose-500 mb-2">{orchestrationsError}</p>
                )}
                <div className="space-y-1 max-h-40 overflow-y-auto mb-4">
                  {orchestrationsLoading ? (
                    <p className={`text-[10px] ${colors.text.muted} italic py-2`}>Loading workflows...</p>
                  ) : filteredOrchestrations.length === 0 ? (
                    <p className={`text-[10px] ${colors.text.muted} italic py-2`}>
                      {orchestrations.length === 0 ? 'No workflows available.' : 'No workflows match your search.'}
                    </p>
                  ) : (
                    filteredOrchestrations.map((orch) => {
                      const selected = isOrchestrationSelected(orch.id);
                      return (
                        <button
                          key={orch.id}
                          type="button"
                          onClick={() => selectOrchestration(orch.id)}
                          className={`w-full text-left p-2 rounded-md border flex items-start gap-2 transition-colors ${
                            selected ? pro.selectedRow : `${colors.border} ${colors.hover}`
                          }`}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                            selected ? pro.selectedCheck : `${colors.border} ${colors.secondary}`
                          }`}>
                            {selected && <Check className={`w-2.5 h-2.5 ${pro.checkIcon}`} />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`text-[11px] font-medium ${colors.text.primary}`}>{orch.name}</span>
                              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${pro.idBadge}`}>
                                {orch.id}
                              </span>
                              {orch.code && (
                                <span className={`text-[9px] px-1.5 py-0.5 rounded ${pro.badge}`}>{orch.code}</span>
                              )}
                            </div>
                            <p className={`text-[10px] ${colors.text.muted} mt-0.5`}>
                              {orch.description || `Channel: ${orch.defaultChannel || 'sandbox'}`}
                            </p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {selectedOrchestrationMapping ? (
                  <>
                    <p className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-2`}>
                      Workflow Configuration
                    </p>
                    {(() => {
                      const mapping = selectedOrchestrationMapping;
                      const orch = getOrchestration(mapping.orchestrationId);
                      const contractFields = workflowStartContract?.start.fields;
                      const previewFields =
                        contractFields ??
                        mapping.parameters.map((param) => ({
                          id: param.id,
                          key: param.name,
                          label: param.name,
                          dataType: param.dataType || 'text',
                          required: param.required,
                          options: param.values ?? [],
                          helpText: '',
                        }));

                      return (
                        <div className={`rounded-md border ${colors.border} overflow-hidden`}>
                          <div className={`px-2.5 py-2 flex items-center justify-between border-b ${colors.border} ${pro.configHeader}`}>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className={`text-[11px] font-medium ${colors.text.primary} truncate`}>
                                {orch?.name || workflowStartContract?.name || mapping.orchestrationId}
                              </span>
                              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0 ${pro.idBadge}`}>
                                {mapping.orchestrationId}
                              </span>
                              {(workflowStartContract?.channel || orch?.defaultChannel) && (
                                <span className={`text-[9px] px-1.5 py-0.5 rounded shrink-0 ${pro.badge}`}>
                                  {workflowStartContract?.channel || orch?.defaultChannel}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={removeSelectedOrchestration}
                              className={`${pro.deleteIcon} p-0.5 shrink-0`}
                              title="Remove"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>

                          <div className="p-2.5 space-y-3">
                            <div>
                              <label className={labelClass}>Training Phrases / Keywords</label>
                              {mapping.keywords.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-1.5">
                                  {mapping.keywords.map((kw) => (
                                    <span
                                      key={kw}
                                      className={`inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded text-[10px] ${pro.keywordTag}`}
                                    >
                                      {kw}
                                      <button type="button" onClick={() => removeOrchKeyword(mapping.orchestrationId, kw)}>
                                        <X className="w-2.5 h-2.5" />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="flex gap-1">
                                <input
                                  type="text"
                                  value={getOrchKeywordDraft(mapping.orchestrationId)}
                                  onChange={(e) =>
                                    setOrchKeywordDraft((prev) => ({
                                      ...prev,
                                      [mapping.orchestrationId]: e.target.value,
                                    }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      addOrchKeyword(mapping.orchestrationId, getOrchKeywordDraft(mapping.orchestrationId));
                                    }
                                  }}
                                  placeholder="Add phrase or keyword"
                                  className={`flex-1 ${inputClass} py-1 text-[11px]`}
                                />
                                <button
                                  type="button"
                                  onClick={() => addOrchKeyword(mapping.orchestrationId, getOrchKeywordDraft(mapping.orchestrationId))}
                                  className={`px-2 py-1 border rounded-md ${pro.addBtn}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            <div className={`border-t ${colors.border} pt-2.5`}>
                              <label className={labelClass}>Start contract inputs</label>
                              <p className={`text-[10px] ${colors.text.muted} mb-1.5`}>
                                Read-only preview of fields this workflow expects at runtime.
                              </p>
                              {startContractLoading ? (
                                <p className={`text-[10px] ${colors.text.muted} italic py-2`}>Loading start contract...</p>
                              ) : startContractError ? (
                                <p className="text-[10px] text-rose-500 mb-2">{startContractError}</p>
                              ) : previewFields.length === 0 ? (
                                <p className={`text-[10px] ${colors.text.muted} italic py-2 rounded-md border border-dashed px-2 ${colors.border}`}>
                                  No input payload required for this workflow.
                                </p>
                              ) : (
                                <div className="space-y-1.5">
                                  {previewFields.map((field) => (
                                    <div
                                      key={field.id || field.key}
                                      className={`rounded-md border p-2 opacity-80 ${colors.border} ${colors.secondary}`}
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                          <p className={`text-[11px] font-medium ${colors.text.primary} truncate`}>
                                            {field.label || field.key}
                                          </p>
                                          <p className={`text-[10px] font-mono ${colors.text.muted}`}>{field.key}</p>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                          <span className={`text-[9px] px-1.5 py-0.5 rounded ${pro.idBadge}`}>
                                            {field.dataType || 'text'}
                                          </span>
                                          <span
                                            className={`text-[9px] px-1.5 py-0.5 rounded border ${
                                              field.required ? pro.paramRequired : pro.paramOptional
                                            }`}
                                          >
                                            {field.required ? 'required' : 'optional'}
                                          </span>
                                        </div>
                                      </div>
                                      {field.helpText ? (
                                        <p className={`text-[10px] ${colors.text.muted} mt-1`}>{field.helpText}</p>
                                      ) : null}
                                      {Array.isArray(field.options) && field.options.length > 0 && (
                                        <p className={`text-[10px] ${colors.text.secondary} mt-1`}>
                                          Options: {field.options.join(', ')}
                                        </p>
                                      )}
                                      <input
                                        type="text"
                                        disabled
                                        readOnly
                                        value={
                                          'defaultValue' in field && field.defaultValue
                                            ? String(field.defaultValue)
                                            : ''
                                        }
                                        placeholder={
                                          ('placeholder' in field && field.placeholder
                                            ? String(field.placeholder)
                                            : field.label || field.key) || '—'
                                        }
                                        className={`${inputClass} mt-1.5 py-1 text-[11px] opacity-70 cursor-not-allowed`}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <p className={`text-[10px] ${colors.text.muted} italic`}>
                    Select one workflow above to review its start-contract inputs and add training phrases.
                  </p>
                )}
              </Card>
            )}

            {/* ── General Search → Knowledge Bases ── */}
            {classification === 'general_search' && (
              <Card
                title="Knowledge Base Routing"
                icon={<BookOpen className="w-3 h-3" />}
                colors={colors}
                iconBoxClass={pro.iconBox}
                badge={
                  <span className={`text-[10px] ${colors.text.muted}`}>{selectedKnowledgeBases.length} selected</span>
                }
                subtitle="Sources searched when user asks general or explanatory questions"
              >
                <div className="relative mb-2.5">
                  <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${colors.text.muted}`} />
                  <input
                    type="text"
                    value={kbSearch}
                    onChange={(e) => setKbSearch(e.target.value)}
                    placeholder="Search knowledge bases..."
                    className={`${inputClass} pl-7`}
                  />
                </div>
                {knowledgeBasesError && (
                  <p className={`text-[10px] text-rose-500 mb-2`}>{knowledgeBasesError}</p>
                )}
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {knowledgeBasesLoading ? (
                    <p className={`text-[10px] ${colors.text.muted} italic py-2`}>Loading knowledge bases...</p>
                  ) : filteredKnowledgeBases.length === 0 ? (
                    <p className={`text-[10px] ${colors.text.muted} italic py-2`}>
                      {knowledgeBases.length === 0 ? 'No knowledge bases available.' : 'No knowledge bases match your search.'}
                    </p>
                  ) : (
                    filteredKnowledgeBases.map((kb) => {
                      const selected = selectedKnowledgeBases.includes(kb.id);
                      return (
                        <button
                          key={kb.id}
                          type="button"
                          onClick={() => toggleKnowledgeBase(kb.id)}
                          className={`w-full text-left p-2 rounded-md border flex items-start gap-2 transition-colors ${
                            selected ? pro.selectedRow : `${colors.border} ${colors.hover}`
                          }`}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                            selected ? pro.selectedCheck : `${colors.border} ${colors.secondary}`
                          }`}>
                            {selected && <Check className={`w-2.5 h-2.5 ${pro.checkIcon}`} />}
                          </div>
                          <div className="min-w-0">
                            <span className={`text-[11px] font-medium ${colors.text.primary}`}>{kb.name}</span>
                            <p className={`text-[10px] ${colors.text.muted}`}>{kb.scope} · {kb.documents} docs</p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </Card>
            )}

            {classification === 'general_search' && (
              <Card title="Search Keywords" icon={<Hash className="w-3 h-3" />} colors={colors} iconBoxClass={pro.iconBox}>
                <label className={labelClass}>Keywords</label>
                {generalSearchKeywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {generalSearchKeywords.map((keyword) => (
                      <span key={keyword} className={`inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded text-[10px] ${pro.keywordTag}`}>
                        {keyword}
                        <button type="button" onClick={() => removeGeneralSearchKeyword(keyword)}>
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={generalSearchKeywordDraft}
                    onChange={(e) => setGeneralSearchKeywordDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addGeneralSearchKeyword(generalSearchKeywordDraft);
                      }
                    }}
                    placeholder="Add keyword for general search routing"
                    className={`flex-1 ${inputClass} py-1 text-[11px]`}
                  />
                  <button
                    type="button"
                    onClick={() => addGeneralSearchKeyword(generalSearchKeywordDraft)}
                    className={`px-2 py-1 border rounded-md ${pro.addBtn}`}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </Card>
            )}

            {/* ── Analytics Hub and Live Reports ── */}
            {(classification === 'requesting_report' || classification === 'live_report') && (
              <>
                {classification === 'live_report' && (
                  <Card
                    title="Live Report Data Model"
                    icon={<Database className="w-3 h-3" />}
                    colors={colors}
                    iconBoxClass={pro.iconBox}
                    subtitle="Data source queried in real time"
                  >
                    <div>
                      <label className={labelClass}>Data Model</label>
                      {dataModelsError && (
                        <p className="text-[10px] text-rose-500 mb-2">{dataModelsError}</p>
                      )}
                      {dataModelsLoading ? (
                        <p className={`text-[10px] ${colors.text.muted} italic py-2`}>Loading data models...</p>
                      ) : dataModels.length === 0 ? (
                        <p className={`text-[10px] ${colors.text.muted} italic py-2`}>No data models available.</p>
                      ) : (
                        <>
                          <select
                            value={selectedDataModel}
                            onChange={(e) => setSelectedDataModel(e.target.value)}
                            className={selectClass}
                          >
                            {dataModels.map((dm) => (
                              <option key={dm.id} value={dm.id}>
                                {dm.name} ({dm.source})
                              </option>
                            ))}
                          </select>
                          {selectedDataModel && (
                            <div className={`mt-2 p-2 rounded-md border ${colors.border} ${colors.secondary}`}>
                              {(() => {
                                const dm = dataModels.find((d) => d.id === selectedDataModel);
                                if (!dm) return null;
                                return (
                                  <>
                                    <p className={`text-[10px] ${colors.text.muted}`}>
                                      {dm.entities.length > 0 ? `Tags: ${dm.entities.join(', ')}` : 'No tags'}
                                    </p>
                                    {dm.entities.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1.5">
                                        {dm.entities.map((e) => (
                                          <span key={e} className={`text-[9px] px-1.5 py-0.5 rounded ${pro.idBadge}`}>
                                            {e}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </Card>
                )}

                {classification === 'requesting_report' && (
                  <Card
                    title="Analytics Hub Reports"
                    icon={<FileText className="w-3 h-3" />}
                    colors={colors}
                    iconBoxClass={pro.iconBox}
                    badge={
                      <span className={`text-[10px] ${colors.text.muted}`}>{selectedReportMappings.length} selected</span>
                    }
                  >
                    <div className="relative mb-2.5">
                      <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${colors.text.muted}`} />
                      <input
                        type="text"
                        value={reportSearch}
                        onChange={(e) => setReportSearch(e.target.value)}
                        placeholder="Search reports..."
                        className={`${inputClass} pl-7`}
                      />
                    </div>

                    <p className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-1.5`}>Catalog</p>
                    {reportCatalogError && (
                      <p className="text-[10px] text-rose-500 mb-2">{reportCatalogError}</p>
                    )}
                    <div className="space-y-1 max-h-40 overflow-y-auto mb-4">
                      {reportCatalogLoading ? (
                        <p className={`text-[10px] ${colors.text.muted} italic py-2`}>Loading reports...</p>
                      ) : filteredCatalogReports.length === 0 ? (
                        <p className={`text-[10px] ${colors.text.muted} italic py-2`}>
                          {reportCatalog.length === 0 ? 'No reports available.' : 'No reports match your search.'}
                        </p>
                      ) : (
                        filteredCatalogReports.map((report) => {
                          const selected = isReportSelected(report.id);
                          return (
                            <button
                              key={report.id}
                              type="button"
                              onClick={() => toggleReportSelection(report.id)}
                              className={`w-full text-left p-2 rounded-md border flex items-start gap-2 ${
                                selected ? pro.selectedRow : `${colors.border} ${colors.hover}`
                              }`}
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                selected ? pro.selectedCheck : `${colors.border} ${colors.secondary}`
                              }`}>
                                {selected && <Check className={`w-2.5 h-2.5 ${pro.checkIcon}`} />}
                              </div>
                              <div className="min-w-0">
                                <span className={`text-[11px] font-medium ${colors.text.primary}`}>{report.name}</span>
                                <p className={`text-[10px] ${colors.text.muted}`}>{report.description}</p>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>

                    {selectedReportMappings.length > 0 && (
                      <>
                        <p className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-2`}>
                          Report Configuration
                        </p>
                        <div className="space-y-3">
                          {selectedReportMappings.map((mapping) => {
                            const report = getCatalogReport(mapping.reportId);
                            if (!report) return null;
                            const draft = getParamDraft(mapping.reportId);
                            return (
                              <div key={mapping.reportId} className={`rounded-md border ${colors.border} overflow-hidden`}>
                                <div className={`px-2.5 py-2 flex items-center justify-between border-b ${colors.border} ${pro.configHeader}`}>
                                  <span className={`text-[11px] font-medium ${colors.text.primary}`}>{report.name}</span>
                                  <button type="button" onClick={() => removeSelectedReport(mapping.reportId)} className={`${pro.deleteIcon} p-0.5`}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                                <div className="p-2.5 space-y-3">
                                  <div>
                                    <label className={labelClass}>Keywords</label>
                                    <div className="flex flex-wrap gap-1 mb-1.5">
                                      {mapping.keywords.map((kw) => (
                                        <span key={kw} className={`inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded text-[10px] ${pro.keywordTag}`}>
                                          {kw}
                                          <button type="button" onClick={() => removeReportKeyword(mapping.reportId, kw)}>
                                            <X className="w-2.5 h-2.5" />
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                    <div className="flex gap-1">
                                      <input
                                        type="text"
                                        value={keywordDraft[mapping.reportId] ?? ''}
                                        onChange={(e) => setKeywordDraft((p) => ({ ...p, [mapping.reportId]: e.target.value }))}
                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addReportKeyword(mapping.reportId, keywordDraft[mapping.reportId] ?? ''))}
                                        onBlur={() => {
                                          const draft = keywordDraft[mapping.reportId] ?? '';
                                          if (draft.trim()) addReportKeyword(mapping.reportId, draft);
                                        }}
                                        placeholder="Add keyword"
                                        className={`flex-1 ${inputClass} py-1`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => addReportKeyword(mapping.reportId, keywordDraft[mapping.reportId] ?? '')}
                                        className={`px-2 py-1 border rounded-md text-[10px] ${pro.addBtn}`}
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  <div>
                                    <label className={labelClass}>Parameters</label>
                                    <div className="flex flex-wrap gap-1 mb-1.5">
                                      {mapping.parameters.map((param) => (
                                        <span
                                          key={param.id}
                                          className={`inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded text-[10px] font-mono border ${
                                            param.required ? pro.paramRequired : pro.paramOptional
                                          }`}
                                        >
                                          {param.name}
                                          <button type="button" onClick={() => toggleParameterRequired(mapping.reportId, param.id)} className="text-[8px] uppercase px-0.5">
                                            {param.required ? 'req' : 'opt'}
                                          </button>
                                          <button type="button" onClick={() => removeReportParameter(mapping.reportId, param.id)}>
                                            <X className="w-2.5 h-2.5" />
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                    <div className="flex gap-1">
                                      <input
                                        type="text"
                                        value={draft.name}
                                        onChange={(e) => setParamDraftForReport(mapping.reportId, { name: e.target.value })}
                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addReportParameter(mapping.reportId, draft.name, draft.required))}
                                        onBlur={() => {
                                          if (draft.name.trim()) addReportParameter(mapping.reportId, draft.name, draft.required);
                                        }}
                                        placeholder="param_name"
                                        className={`flex-1 ${inputClass} font-mono py-1`}
                                      />
                                      <label className={`flex items-center gap-1 px-2 border ${colors.border} ${colors.secondary} rounded-md text-[10px] cursor-pointer ${colors.text.secondary}`}>
                                        <input type="checkbox" checked={draft.required} onChange={(e) => setParamDraftForReport(mapping.reportId, { required: e.target.checked })} className={pro.checkbox} />
                                        Req
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() => addReportParameter(mapping.reportId, draft.name, draft.required)}
                                        className={`px-2 py-1 border rounded-md ${pro.addBtn}`}
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </Card>
                )}

                {classification === 'live_report' && (
                  <Card
                    title="Additional Context Instructions"
                    icon={<FileText className="w-3 h-3" />}
                    colors={colors}
                    iconBoxClass={pro.iconBox}
                    subtitle="Optional guidance applied when generating the live report"
                  >
                    <textarea
                      value={liveReportInstructions}
                      onChange={(e) => setLiveReportInstructions(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      placeholder="Add context, business rules, filtering guidance, or response-format instructions..."
                      className={`${inputClass} resize-y`}
                    />
                    <p className={`mt-1 text-right text-[9px] ${colors.text.muted}`}>
                      {liveReportInstructions.length}/2000
                    </p>
                  </Card>
                )}

                {classification === 'live_report' && (
                  <Card title="Live Report Parameters" icon={<Sliders className="w-3 h-3" />} colors={colors} iconBoxClass={pro.iconBox}>
                    <p className={`text-[10px] ${colors.text.muted} mb-2`}>
                      Define keywords and parameters extracted from user messages for live data model queries.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className={labelClass}>Keywords</label>
                        {liveDataModelMapping.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {liveDataModelMapping.keywords.map((keyword) => (
                              <span key={keyword} className={`inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded text-[10px] ${pro.keywordTag}`}>
                                {keyword}
                                <button type="button" onClick={() => removeLiveKeyword(keyword)}>
                                  <X className="w-2.5 h-2.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={liveKeywordDraft}
                            onChange={(e) => setLiveKeywordDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addLiveKeyword(liveKeywordDraft);
                              }
                            }}
                            placeholder="Add keyword"
                            className={`flex-1 ${inputClass} py-1 text-[11px]`}
                          />
                          <button type="button" onClick={() => addLiveKeyword(liveKeywordDraft)} className={`px-2 py-1 border rounded-md ${pro.addBtn}`}>
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Parameters</label>
                        <div className="mb-1.5 hidden grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)_auto_28px] gap-2 px-2 sm:grid">
                          <span className={`text-[9px] uppercase tracking-wide ${colors.text.muted}`}>Parameter name</span>
                          <span className={`text-[9px] uppercase tracking-wide ${colors.text.muted}`}>Type</span>
                          <span className={`text-[9px] uppercase tracking-wide ${colors.text.muted}`}>Claim / data type</span>
                          <span className={`text-[9px] uppercase tracking-wide ${colors.text.muted}`}>Required</span>
                          <span />
                        </div>

                        <div className="mb-2 space-y-1.5">
                          {liveDataModelMapping.parameters.length === 0 && (
                            <p className={`rounded-md border border-dashed px-2 py-2 text-[10px] ${colors.border} ${colors.text.muted}`}>
                              No parameters configured.
                            </p>
                          )}
                          {liveDataModelMapping.parameters.map((param) => (
                            <div
                              key={param.id}
                              className={`grid grid-cols-1 items-center gap-2 rounded-md border p-2 sm:grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)_auto_28px] ${colors.border} ${colors.secondary}`}
                            >
                              <span className={`truncate font-mono text-[10px] ${colors.text.primary}`}>{param.name}</span>
                              <span className={`text-[10px] ${colors.text.secondary}`}>
                                {(param.parameterType ?? (param.claim ? 'claim' : 'parameter')) === 'claim'
                                  ? 'Claims'
                                  : 'Parameter'}
                              </span>
                              <span className={`truncate font-mono text-[10px] ${colors.text.secondary}`}>
                                {(param.parameterType ?? (param.claim ? 'claim' : 'parameter')) === 'claim'
                                  ? param.claim || '—'
                                  : param.dataType === 'array' && param.values?.length
                                    ? `array [${param.values.join(', ')}]`
                                    : param.dataType || 'string'}
                              </span>
                              <label className={`flex items-center gap-1.5 text-[10px] ${colors.text.secondary} ${
                                (param.parameterType ?? (param.claim ? 'claim' : 'parameter')) === 'claim'
                                  ? 'cursor-not-allowed opacity-50'
                                  : ''
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={param.required}
                                  onChange={() => toggleLiveParameterRequired(param.id)}
                                  disabled={(param.parameterType ?? (param.claim ? 'claim' : 'parameter')) === 'claim'}
                                  className={pro.checkbox}
                                />
                                Required
                              </label>
                              <button
                                type="button"
                                onClick={() => removeLiveParameter(param.id)}
                                className={`rounded p-1 ${pro.deleteIcon}`}
                                aria-label={`Remove parameter ${param.name}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className={`grid grid-cols-1 items-start gap-2 rounded-md border p-2 sm:grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)_auto_28px] ${colors.border}`}>
                          <input
                            type="text"
                            value={liveParamDraft.name}
                            onChange={(e) => setLiveParamDraft((prev) => ({ ...prev, name: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addLiveParameter();
                              }
                            }}
                            placeholder="Parameter name"
                            aria-label="Parameter name"
                            className={`${inputClass} font-mono py-1`}
                          />

                          <select
                            value={liveParamDraft.parameterType}
                            onChange={(e) => {
                              const parameterType = e.target.value as LiveParameterType;
                              setLiveParamDraft((prev) => ({
                                ...prev,
                                parameterType,
                                required: parameterType === 'claim' ? false : prev.required,
                              }));
                            }}
                            aria-label="Parameter type"
                            className={`${selectClass} py-1`}
                          >
                            <option value="claim">Claims</option>
                            <option value="parameter">Parameter</option>
                          </select>

                          <div className="space-y-1.5">
                            {liveParamDraft.parameterType === 'claim' ? (
                              <>
                                <select
                                  value={liveParamDraft.claimOption}
                                  onChange={(e) =>
                                    setLiveParamDraft((prev) => ({
                                      ...prev,
                                      claimOption: e.target.value,
                                      customClaim: e.target.value === CUSTOM_CLAIM_OPTION ? prev.customClaim : '',
                                      required: false,
                                    }))
                                  }
                                  aria-label="Claim source"
                                  className={`${selectClass} py-1`}
                                >
                                  {LIVE_REPORT_CLAIM_OPTIONS.map((claim) => (
                                    <option key={claim} value={claim}>{claim}</option>
                                  ))}
                                  <option value={CUSTOM_CLAIM_OPTION}>Custom claim</option>
                                </select>
                                {liveParamDraft.claimOption === CUSTOM_CLAIM_OPTION && (
                                  <input
                                    type="text"
                                    value={liveParamDraft.customClaim}
                                    onChange={(e) =>
                                      setLiveParamDraft((prev) => ({
                                        ...prev,
                                        customClaim: e.target.value,
                                        required: false,
                                      }))
                                    }
                                    placeholder="$custom_claim"
                                    aria-label="Custom claim"
                                    className={`${inputClass} font-mono py-1`}
                                  />
                                )}
                                {liveParamDraft.claimOption === CUSTOM_CLAIM_OPTION && (
                                  <p className={`text-[9px] leading-snug ${colors.text.muted}`}>
                                    Custom claims must start with <span className="font-mono">$</span>.
                                  </p>
                                )}
                              </>
                            ) : (
                              <>
                                <select
                                  value={liveParamDraft.dataType}
                                  onChange={(e) =>
                                    setLiveParamDraft((prev) => ({
                                      ...prev,
                                      dataType: e.target.value,
                                      arrayValues: e.target.value === 'array' ? prev.arrayValues : [],
                                      arrayValueDraft: e.target.value === 'array' ? prev.arrayValueDraft : '',
                                    }))
                                  }
                                  aria-label="Parameter data type"
                                  className={`${selectClass} py-1`}
                                >
                                  {LIVE_REPORT_DATA_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>

                                {liveParamDraft.dataType === 'array' && (
                                  <div className="space-y-1.5">
                                    {liveParamDraft.arrayValues.length > 0 && (
                                      <div className="flex flex-wrap gap-1">
                                        {liveParamDraft.arrayValues.map((value) => (
                                          <span
                                            key={value}
                                            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] ${pro.keywordTag}`}
                                          >
                                            {value}
                                            <button
                                              type="button"
                                              onClick={() => removeLiveArrayValue(value)}
                                              aria-label={`Remove array value ${value}`}
                                            >
                                              <X className="h-2.5 w-2.5" />
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    <div className="flex gap-1">
                                      <input
                                        type="text"
                                        value={liveParamDraft.arrayValueDraft}
                                        onChange={(e) =>
                                          setLiveParamDraft((prev) => ({
                                            ...prev,
                                            arrayValueDraft: e.target.value,
                                          }))
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addLiveArrayValue();
                                          }
                                        }}
                                        placeholder="Add array value"
                                        aria-label="Array value"
                                        className={`${inputClass} py-1`}
                                      />
                                      <button
                                        type="button"
                                        onClick={addLiveArrayValue}
                                        disabled={!liveParamDraft.arrayValueDraft.trim()}
                                        className={`flex w-7 items-center justify-center rounded-md border disabled:cursor-not-allowed disabled:opacity-40 ${pro.addBtn}`}
                                        aria-label="Add array value"
                                      >
                                        <Plus className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          <label className={`flex min-h-[30px] items-center gap-1.5 whitespace-nowrap px-1 text-[10px] ${colors.text.secondary} ${
                            isLiveParameterClaim ? 'cursor-not-allowed opacity-50' : ''
                          }`}>
                            <input
                              type="checkbox"
                              checked={liveParamDraft.required}
                              onChange={(e) => setLiveParamDraft((prev) => ({ ...prev, required: e.target.checked }))}
                              disabled={isLiveParameterClaim}
                              className={pro.checkbox}
                            />
                            Required
                          </label>
                          <button
                            type="button"
                            onClick={addLiveParameter}
                            disabled={
                              !liveParamDraft.name.trim() ||
                              (liveParamDraft.parameterType === 'claim' &&
                                liveParamDraft.claimOption === CUSTOM_CLAIM_OPTION &&
                                (!liveParamDraft.customClaim.trim().startsWith('$') ||
                                  liveParamDraft.customClaim.trim().length <= 1)) ||
                              (liveParamDraft.parameterType === 'parameter' &&
                                liveParamDraft.dataType === 'array' &&
                                getPendingLiveArrayValues().length === 0)
                            }
                            className={`flex h-[30px] w-7 items-center justify-center rounded-md border disabled:cursor-not-allowed disabled:opacity-40 ${pro.addBtn}`}
                            aria-label="Add live report parameter"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
              </>
            )}

            {/* Training Phrases */}
            <Card title="Training Phrases" icon={<MessageSquare className="w-3 h-3" />} colors={colors} iconBoxClass={pro.iconBox}>
              <div className="space-y-1.5">
                {trainingPhrases.map((phrase, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-mono w-4 text-right ${colors.text.muted}`}>{index + 1}</span>
                    <input
                      type="text"
                      value={phrase}
                      onChange={(e) =>
                        setTrainingPhrases((prev) => prev.map((p, i) => (i === index ? e.target.value : p)))
                      }
                      className={`flex-1 ${inputClass}`}
                    />
                    <button
                      type="button"
                      onClick={() => setTrainingPhrases((prev) => prev.filter((_, i) => i !== index))}
                      className={`p-1 ${pro.deleteIcon}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setTrainingPhrases((prev) => [...prev, ''])}
                className={`mt-2 w-full py-1.5 border border-dashed text-[10px] rounded-md flex items-center justify-center gap-1 ${pro.dashedAdd}`}
              >
                <Plus className="w-3 h-3" /> Add phrase
              </button>
            </Card>

            {/* Confirmation */}
            <Card title="Fallback & Confirmation" icon={<ShieldCheck className="w-3 h-3" />} colors={colors} iconBoxClass={pro.iconBox}>
              <div className="space-y-3">
                <Toggle checked={confirmBelowThreshold} onChange={setConfirmBelowThreshold} label="Confirm when confidence is low" colors={colors} toggleOnClass={pro.toggleOn} toggleOffClass={pro.toggleOff} />
                {confirmBelowThreshold && (
                  <div className="flex items-center gap-2 pl-1">
                    <input type="range" min={50} max={95} value={confirmationThreshold} onChange={(e) => setConfirmationThreshold(Number(e.target.value))} className="flex-1 accent-slate-600 h-1" />
                    <span className={`text-[10px] font-mono ${colors.text.primary}`}>{confirmationThreshold}%</span>
                  </div>
                )}
                <div className={`p-2 rounded-md border ${colors.border} space-y-2`}>
                  <Toggle checked={confirmMultipleRoutes} onChange={setConfirmMultipleRoutes} label="Confirm when multiple routes match" colors={colors} toggleOnClass={pro.toggleOn} toggleOffClass={pro.toggleOff} />
                  <Toggle checked={confirmMissingParams} onChange={setConfirmMissingParams} label="Confirm when parameters are missing" colors={colors} toggleOnClass={pro.toggleOn} toggleOffClass={pro.toggleOff} />
                </div>
                <div>
                  <label className={labelClass}>Fallback message</label>
                  <textarea value={confirmationMessage} onChange={(e) => setConfirmationMessage(e.target.value)} rows={2} className={inputClass} />
                </div>
              </div>
            </Card>
          </div>

          {/* Test panel */}
          <div className="xl:sticky xl:top-[52px] xl:self-start">
            <section className={`${colors.surface} border ${colors.border} rounded-lg overflow-hidden`}>
              <div className={`px-3 py-2 border-b ${colors.border} flex items-center gap-2`}>
                <FlaskConical className={`w-3.5 h-3.5 ${pro.cardSelectedIcon}`} />
                <h2 className={`text-xs font-semibold ${colors.text.primary}`}>Classification Preview</h2>
              </div>
              <div className="p-3 space-y-2.5">
                <div className={`p-2 rounded-md border ${colors.border} ${colors.secondary}`}>
                  <p className={`text-[10px] ${colors.text.muted}`}>Active classification</p>
                  <p className={`text-[11px] font-medium ${colors.text.primary} mt-0.5`}>{selectedClassification.label}</p>
                </div>
                <div>
                  <label className={labelClass}>Sample message</label>
                  <textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Type a user message..."
                    rows={3}
                    className={inputClass}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAnalyze}
                  className={`w-full py-1.5 border text-[11px] rounded-md flex items-center justify-center gap-1.5 font-medium ${pro.btnGhost}`}
                >
                  <FlaskConical className="w-3 h-3" /> Analyze
                </button>
                {lastSimulationSummary ? (
                  <div className={`p-2.5 rounded-md border ${colors.border} ${colors.secondary}`}>
                    <p className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted}`}>
                      Last simulation
                    </p>
                    <p className={`text-[11px] font-medium ${colors.text.primary} mt-1`}>{lastSimulationSummary}</p>
                    <button
                      type="button"
                      onClick={handleAnalyze}
                      className={`mt-2 text-[10px] ${colors.text.secondary} underline hover:no-underline`}
                    >
                      Run again
                    </button>
                  </div>
                ) : (
                  <p className={`text-[10px] text-center py-4 ${colors.text.muted}`}>
                    Run analyze to open the routing pipeline simulation
                  </p>
                )}
              </div>
            </section>
          </div>
        </div>
        )}
      </div>

      <IntentAnalysisSimulation
        open={simulationOpen}
        onClose={() => setSimulationOpen(false)}
        theme={theme}
        colors={colors}
        runToken={simulationRunToken}
        context={simulationContext}
        onComplete={(summary) => setLastSimulationSummary(summary)}
      />
    </div>
  );
};

export default IntentDefinition;
